<template>
  <div class="topn-monitoring-container mb-6" v-if="isAdmin">
    <!-- 6 Role Summary Cards (Image 2 style) -->
    <div class="approval-nodes-grid">
      <div 
        v-for="role in rolesList" 
        :key="role.key"
        class="role-node-card"
        :class="{ 'card-selected': selectedRoleKey === role.key }"
        @click="selectRole(role.key)"
        :title="`${role.label} bo'yicha kafedralar reytingini ko'rish`"
      >
        <div class="node-icon-circle">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        </div>
        <div class="node-info">
          <div class="node-title">{{ role.label }}</div>
          <div class="node-stats">
            <span class="node-pct">{{ getRolePercent(role.key) }}%</span>
            <span class="node-count">({{ getRoleCount(role.key) }} / {{ totalPlans }} ta)</span>
          </div>
          <div class="node-progress-bar">
            <div class="node-progress-fill" :style="{ width: getRolePercent(role.key) + '%' }"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Kafedralar (Top-N) Card (Image 3 style) -->
    <div class="chart-main-card mt-4">
      <div class="chart-header flex flex-wrap justify-between items-center gap-3">
        <div class="flex items-center gap-2">
          <span class="chart-title-tag">Kafedralar (Top-N)</span>
          <span class="active-role-badge">{{ activeRoleLabel }} tasdiqlaganlar</span>
        </div>

        <div class="chart-controls flex items-center gap-3">
          <!-- Top-N Selector -->
          <div class="select-wrapper">
            <select v-model="topNLimit" class="topn-select">
              <option :value="5">Top 5</option>
              <option :value="10">Top 10</option>
              <option :value="15">Top 15</option>
              <option :value="20">Top 20</option>
              <option :value="999">Barchasi</option>
            </select>
          </div>

          <!-- Search Box -->
          <div class="chart-search-box">
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Qidirish..." 
              class="chart-search-input" 
            />
            <span class="search-icon">🔍</span>
          </div>

          <!-- Download Excel -->
          <button class="btn-download" @click="exportToExcel" title="Excel formatida yuklab olish">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </button>
        </div>
      </div>

      <!-- Legend -->
      <div class="chart-legend">
        <span class="legend-color-box"></span>
        <span class="legend-text">{{ oquvYili }} o'quv yili</span>
      </div>

      <!-- SVG Chart Area with Horizontal Scroll -->
      <div class="chart-scroll-wrapper" ref="chartScroll">
        <div v-if="loading" class="chart-loading py-16 text-center text-gray-400">
          <span class="spinner-inline"></span> Ma'lumotlar yuklanmoqda...
        </div>
        <div v-else-if="sortedDepts.length === 0" class="chart-empty py-16 text-center text-gray-400">
          Kafedralar bo'yicha ma'lumot topilmadi
        </div>
        <svg 
          v-else 
          :width="chartWidth" 
          :height="chartHeight" 
          class="topn-svg-chart"
        >
          <defs>
            <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#0284c7" />
              <stop offset="100%" stop-color="#0369a1" />
            </linearGradient>
            <linearGradient id="barHoverGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#38bdf8" />
              <stop offset="100%" stop-color="#0284c7" />
            </linearGradient>
            <filter id="barShadow" x="-10%" y="-10%" width="120%" height="120%">
              <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.15"/>
            </filter>
          </defs>

          <!-- Y-Axis Grid Lines & Labels (0, 20, 40, 60, 80, 100) -->
          <g class="y-grid">
            <template v-for="gridVal in [100, 80, 60, 40, 20, 0]" :key="gridVal">
              <line 
                :x1="paddingLeft" 
                :y1="getYPos(gridVal)" 
                :x2="chartWidth - paddingRight" 
                :y2="getYPos(gridVal)" 
                stroke="#e2e8f0" 
                stroke-width="1"
                :stroke-dasharray="gridVal === 0 ? '' : '3 3'"
              />
              <text 
                :x="paddingLeft - 10" 
                :y="getYPos(gridVal) + 4" 
                text-anchor="end" 
                class="axis-text"
              >{{ gridVal }}</text>
            </template>
          </g>

          <!-- Smooth Spline Trend Line -->
          <path 
            v-if="trendPath" 
            :d="trendPath" 
            fill="none" 
            stroke="#93c5fd" 
            stroke-width="2" 
            class="trend-line"
          />

          <!-- Bars and Labels for each Kafedra -->
          <g v-for="(dept, idx) in sortedDepts" :key="dept.departmentId || idx">
            <!-- The Bar Column -->
            <rect 
              :x="getBarX(idx)" 
              :y="getBarY(dept.pct)" 
              :width="barWidth" 
              :height="getBarHeight(dept.pct)" 
              rx="4" 
              ry="4"
              class="bar-rect"
              :class="{ 'bar-active': hoveredIdx === idx }"
              filter="url(#barShadow)"
              @mouseenter="onHoverBar(dept, idx, $event)"
              @mouseleave="hoveredIdx = null"
            />

            <!-- Percentage Label above Bar -->
            <text 
              :x="getBarX(idx) + barWidth / 2" 
              :y="getBarY(dept.pct) - 8" 
              text-anchor="middle" 
              class="bar-pct-text"
            >{{ dept.pct }}%</text>

            <!-- Department Name below Bar (Rotated at 45 degrees, smart 2-line wrapped to fit perfectly) -->
            <g 
              :transform="`translate(${getBarX(idx) + barWidth / 2}, ${chartPlotHeight + paddingTop + 15}) rotate(-45)`"
              class="dept-name-group"
              @mouseenter="onHoverBar(dept, idx, $event)"
              @mouseleave="hoveredIdx = null"
            >
              <title>{{ dept.departmentName }}</title>
              <text 
                text-anchor="end" 
                class="dept-name-text"
              >
                <template v-if="getDeptNameLines(dept.departmentName).length === 1">
                  <tspan x="0" y="4">{{ getDeptNameLines(dept.departmentName)[0] }}</tspan>
                </template>
                <template v-else>
                  <tspan x="0" y="-4">{{ getDeptNameLines(dept.departmentName)[0] }}</tspan>
                  <tspan x="0" y="11">{{ getDeptNameLines(dept.departmentName)[1] }}</tspan>
                </template>
              </text>
            </g>
          </g>
        </svg>

        <!-- Floating Tooltip -->
        <div 
          v-if="hoveredDept" 
          class="chart-tooltip" 
          :style="{ left: tooltipX + 'px', top: tooltipY + 'px' }"
        >
          <div class="tooltip-title">{{ hoveredDept.departmentName }}</div>
          <div class="tooltip-row">
            <span>{{ activeRoleLabel }}:</span>
            <strong>{{ hoveredDept.count }} / {{ hoveredDept.total }} ta ({{ hoveredDept.pct }}%)</strong>
          </div>
          <div class="tooltip-row text-xs text-gray-400">
            Jami o'qituvchilar: {{ hoveredDept.total }} ta
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import * as XLSX from "xlsx";

const API = process.env.VUE_APP_API_URL || "http://localhost:3300/api";

export default {
  name: "KafedralarTopNChart",
  props: {
    userRole: {
      type: String,
      default: ""
    },
    oquvYili: {
      type: String,
      default: "2026-2027"
    }
  },
  data() {
    return {
      loading: false,
      totalPlans: 0,
      overallStats: {},
      departmentsData: [],
      selectedRoleKey: "kafedra", // default role to highlight
      topNLimit: 15,
      searchQuery: "",
      hoveredIdx: null,
      hoveredDept: null,
      tooltipX: 0,
      tooltipY: 0,
      
      paddingTop: 40,
      paddingBottom: 210, // ample space for 45deg department names
      paddingLeft: 120, // ample space on left so first bar's name never clips
      paddingRight: 60,
      barWidth: 38,
      chartPlotHeight: 230,

      rolesList: [
        { key: "teacher", label: "O'qituvchi" },
        { key: "kafedra", label: "Kafedra" },
        { key: "dekan", label: "Dekan" },
        { key: "yoshlar", label: "Yoshlar ishlari bo'yicha prorektor" },
        { key: "ilmiy", label: "Ilmiy ishlari bo'yicha prorektor" },
        { key: "oquv", label: "O'quv ishlari bo'yicha prorektor" }
      ]
    };
  },
  computed: {
    isAdmin() {
      const r = (this.userRole || "").trim().toLowerCase();
      return ["super admin", "superadmin", "admin", "administrator"].includes(r);
    },
    activeRoleLabel() {
      const found = this.rolesList.find(r => r.key === this.selectedRoleKey);
      return found ? found.label : "Kafedra";
    },
    effectiveBarGap() {
      const count = this.sortedDepts.length;
      if (count <= 5) return 80;
      if (count <= 10) return 52;
      return 38;
    },
    chartHeight() {
      return this.paddingTop + this.chartPlotHeight + this.paddingBottom;
    },
    chartWidth() {
      const count = this.sortedDepts.length;
      const minW = 950;
      const calcW = this.paddingLeft + count * (this.barWidth + this.effectiveBarGap) + this.paddingRight;
      return Math.max(minW, calcW);
    },
    sortedDepts() {
      let list = this.departmentsData
        .filter(d => {
          const name = (d.departmentName || "").toLowerCase().trim();
          const id = Number(d.departmentId);
          if (!id || id <= 0) return false;
          if (name.includes("noma'lum") || name.includes("nomalum") || name.includes("kafedra #0")) return false;
          if (name.includes("xodimlar") || id === 46) return false;
          if (name.includes("rektorat") || id === 58) return false;
          if (name.includes("fakultet") || id === 65) return false;
          if (id === 8 || id === 22 || name.includes("avtomatlashtirish va boshqarish") || name.includes("meva-sabzavot")) return false;
          return true;
        })
        .map(d => {
          const pct = d.percentages ? (d.percentages[this.selectedRoleKey] || 0) : 0;
          const count = d.counts ? (d.counts[this.selectedRoleKey] || 0) : 0;
          return {
            ...d,
            pct: Number(pct),
            count
          };
        });

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter(d => (d.departmentName || "").toLowerCase().includes(q));
      }

      // Sort by percentage descending
      list.sort((a, b) => b.pct - a.pct);

      if (this.topNLimit && this.topNLimit < 999) {
        list = list.slice(0, this.topNLimit);
      }

      return list;
    },
    trendPath() {
      if (this.sortedDepts.length < 2) return "";
      const points = this.sortedDepts.map((d, i) => {
        const x = this.getBarX(i) + this.barWidth / 2;
        const y = this.getBarY(d.pct);
        return { x, y };
      });

      let d = `M ${points[0].x} ${points[0].y}`;
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i];
        const p1 = points[i + 1];
        const cx = (p0.x + p1.x) / 2;
        d += ` C ${cx} ${p0.y}, ${cx} ${p1.y}, ${p1.x} ${p1.y}`;
      }
      return d;
    }
  },
  mounted() {
    this.fetchStats();
  },
  methods: {
    async fetchStats() {
      this.loading = true;
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/shaxsiy-reja/monitoring-stats`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { oquvYili: this.oquvYili }
        });
        if (res.data.success && res.data.data) {
          const d = res.data.data;
          this.totalPlans = d.totalPlans || 0;
          this.overallStats = d.overall || {};
          this.departmentsData = d.departments || [];
        }
      } catch (err) {
        console.error("Error fetching monitoring stats:", err);
      } finally {
        this.loading = false;
      }
    },
    getRolePercent(key) {
      if (this.overallStats[key]) {
        return this.overallStats[key].percent || 0;
      }
      return 0;
    },
    getRoleCount(key) {
      if (this.overallStats[key]) {
        return this.overallStats[key].approved || 0;
      }
      return 0;
    },
    selectRole(key) {
      this.selectedRoleKey = key;
    },
    getYPos(val) {
      return this.paddingTop + this.chartPlotHeight - (val / 100) * this.chartPlotHeight;
    },
    getBarX(idx) {
      return this.paddingLeft + idx * (this.barWidth + this.effectiveBarGap) + this.effectiveBarGap / 2;
    },
    getBarY(pct) {
      return this.getYPos(pct);
    },
    getBarHeight(pct) {
      return Math.max(2, (pct / 100) * this.chartPlotHeight);
    },
    getDeptNameLines(name) {
      if (!name) return [""];
      name = name.trim();
      if (name.length <= 20) {
        return [name];
      }
      const words = name.split(/\s+/);
      if (words.length === 1) {
        return [name];
      }
      const mid = name.length / 2;
      let bestIdx = 1;
      let bestDiff = 999;
      let currentLen = 0;
      for (let i = 0; i < words.length - 1; i++) {
        currentLen += words[i].length + (i > 0 ? 1 : 0);
        const diff = Math.abs(currentLen - mid);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestIdx = i + 1;
        }
      }
      const line1 = words.slice(0, bestIdx).join(" ");
      const line2 = words.slice(bestIdx).join(" ");
      return [line1, line2];
    },
    onHoverBar(dept, idx, event) {
      this.hoveredIdx = idx;
      this.hoveredDept = dept;
      const rect = this.$refs.chartScroll?.getBoundingClientRect();
      if (rect) {
        this.tooltipX = event.clientX - rect.left + 15;
        this.tooltipY = Math.max(10, event.clientY - rect.top - 60);
      }
    },
    exportToExcel() {
      const data = this.sortedDepts.map((d, i) => ({
        "№": i + 1,
        "Kafedra nomi": d.departmentName,
        "Tasdiqlash bosqichi": this.activeRoleLabel,
        "Tasdiqlanganlar (ta)": d.count,
        "Jami o'qituvchilar (ta)": d.total,
        "Foiz (%)": d.pct + "%"
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Kafedralar_TopN");
      XLSX.writeFile(wb, `Kafedralar_TopN_${this.selectedRoleKey}_${this.oquvYili}.xlsx`);
    }
  }
};
</script>

<style scoped>
.topn-monitoring-container {
  width: 100%;
}

/* 6 Role Approval Nodes (Image 2 style) */
.approval-nodes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.role-node-card {
  background: #fffbeb;
  border: 1px solid #fef08a;
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.role-node-card:hover {
  background: #fef9c3;
  border-color: #fde047;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(217, 119, 6, 0.12);
}

.role-node-card.card-selected {
  background: #fef3c7;
  border-color: #d97706;
  box-shadow: 0 0 0 2px #d97706;
}

.node-icon-circle {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #fde68a;
  color: #b45309;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.role-node-card.card-selected .node-icon-circle {
  background: #d97706;
  color: #ffffff;
}

.node-info {
  flex: 1;
  min-width: 0;
}

.node-title {
  font-size: 13px;
  font-weight: 600;
  color: #78350f;
  line-height: 1.3;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.node-stats {
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-size: 12px;
}

.node-pct {
  font-weight: 700;
  color: #b45309;
  font-size: 14px;
}

.node-count {
  color: #92400e;
  font-size: 11px;
}

.node-progress-bar {
  width: 100%;
  height: 4px;
  background: #fef08a;
  border-radius: 2px;
  margin-top: 4px;
  overflow: hidden;
}

.node-progress-fill {
  height: 100%;
  background: #d97706;
  border-radius: 2px;
  transition: width 0.3s ease;
}

/* Chart Main Card (Image 3 style) */
.chart-main-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  position: relative;
}

.chart-title-tag {
  background: #2563eb;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 4px;
  letter-spacing: 0.3px;
}

.active-role-badge {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.topn-select {
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  background: #f8fafc;
  color: #334155;
  outline: none;
  cursor: pointer;
}

.chart-search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.chart-search-input {
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 6px 12px 6px 28px;
  font-size: 13px;
  background: #f8fafc;
  color: #334155;
  outline: none;
  width: 150px;
  transition: width 0.2s;
}

.chart-search-input:focus {
  width: 200px;
  border-color: #2563eb;
  background: #ffffff;
}

.search-icon {
  position: absolute;
  left: 8px;
  font-size: 11px;
  pointer-events: none;
  opacity: 0.6;
}

.btn-download {
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 6px 10px;
  color: #475569;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.15s;
}

.btn-download:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.chart-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 6px;
  margin-bottom: 8px;
}

.legend-color-box {
  width: 14px;
  height: 10px;
  background: #0284c7;
  border-radius: 2px;
}

.legend-text {
  font-size: 12px;
  font-weight: 500;
  color: #475569;
}

/* SVG Chart */
.chart-scroll-wrapper {
  overflow-x: auto;
  position: relative;
  width: 100%;
}

.topn-svg-chart {
  display: block;
}

.axis-text {
  font-size: 11px;
  fill: #64748b;
  font-family: inherit;
}

.bar-rect {
  fill: url(#barGrad);
  cursor: pointer;
  transition: opacity 0.2s, fill 0.2s;
}

.bar-rect:hover,
.bar-rect.bar-active {
  fill: url(#barHoverGrad);
}

.bar-pct-text {
  font-size: 12px;
  font-weight: 700;
  fill: #0f172a;
  font-family: inherit;
}

.dept-name-group {
  cursor: pointer;
}

.dept-name-text {
  font-size: 11px;
  font-weight: 500;
  fill: #334155;
  font-family: inherit;
  cursor: pointer;
  transition: fill 0.15s;
}

.dept-name-group:hover .dept-name-text,
.dept-name-text:hover {
  fill: #0284c7;
  font-weight: 700;
}

.trend-line {
  pointer-events: none;
}

/* Floating Tooltip */
.chart-tooltip {
  position: absolute;
  background: #0f172a;
  color: #ffffff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  pointer-events: none;
  z-index: 50;
  white-space: nowrap;
}

.tooltip-title {
  font-weight: 700;
  margin-bottom: 4px;
  color: #38bdf8;
}

.tooltip-row {
  line-height: 1.4;
}
</style>
